import SocketIO from 'socket.io-client';

export interface SocketClientListeners {
  onConnect: () => void;
  onDisconnect: () => void;
  onError: (error: any) => void;
  onReconnect: (data: any) => void;
  onReconnectFailed: (error: any) => void;
}

export class SocketClient {
  private _socket: SocketIOClient.Socket;
  private _abort = new AbortController();

  /**
   * @param uri The URI that the socket client can connect to.
   */
  constructor(private readonly uri: string) {
    this._socket = SocketIO(this.uri, this.mergeOptions({}));
  }

  /**
   * The current socket client, if one exists.
   */
  public get socket() {
    return this._socket;
  }

  public readonly setListeners = (listeners: SocketClientListeners) => {
    this._socket.off('connect');
    this._socket.on('connect', () => {
      console.debug('[SocketClient] Connected', this.uri);
      this._abort.abort();
      this._abort = new AbortController();
      listeners.onConnect();
    });

    this._socket.off('disconnect');
    this._socket.on('disconnect', () => {
      console.debug('[SocketClient] Disconnected', this.uri);
      this._abort.abort();
      listeners.onDisconnect();
    });

    this._socket.off('error');
    this._socket.on('error', (err: any) => {
      console.debug('[SocketClient] Error', this.uri, err);
      listeners.onError(err);
    });

    this._socket.off('reconnect');
    this._socket.on('reconnect', (data: any) => {
      console.debug('[SocketClient] Reconnected', this.uri, data);
      listeners.onReconnect(data);
    });

    this._socket.off('reconnect_failed');
    this._socket.on('reconnect_failed', (err: any) => {
      console.debug('[SocketClient] Reconnection failed', this.uri, err);
      listeners.onReconnectFailed(err);
    });
  };

  public readonly updateAuth = (token?: string) => {
    this._socket.io.opts.query = { ...(this._socket.io.opts.query as {}), token };
    this.reconcileAuthState();
  };

  /** Executes a request against the socket server. */
  public readonly request = async <R extends any>(event: string, data: any): Promise<R> => {
    this.reconcileAuthState();
    if (!this._socket.connected) throw new Error(`Socket is not connected, not attempting request for event: ${event}`);

    const response = await new Promise<any>((res, rej) => {
      const abortHandler = () => rej(`request for event: ${event} aborted`);
      this._abort.signal.addEventListener('abort', abortHandler);
      this._socket!.emit(event, data, (data: any) => {
        res(data);
        this._abort.signal.removeEventListener('abort', abortHandler);
      });
    });

    if (response?.error) {
      console.error(`socket request failed for event: ${event}`, response.error);
      throw new Error(response.error);
    }
    return response;
  };

  private readonly reconcileAuthState = () => {
    const token = (this._socket.io.opts.query as any)?.token;
    if (token && !this.socket.connected) {
      this.open();
    }
    if (!token && this.socket.connected) {
      this.close();
    }
  };

  private readonly close = () => {
    try {
      this._abort.abort();
      this._socket.close();
    } catch (e) {
      console.error('failed to close socket connection gracefully', this.uri, e);
    }
  };

  private readonly open = () => {
    this._abort.abort();
    this._abort = new AbortController();
    this._socket.open();
  };

  private readonly mergeOptions = (options: any = {}) => {
    // You may override any default socketio connect options.
    const defaultOptions = {
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1 * 1000,
      reconnectionDelayMax: 10 * 1000,
      autoConnect: false,
      rejectUnauthorized: true,
    };
    return { ...defaultOptions, ...options };
  };
}
